import React, { useState } from "react";
import { Draggable } from "@hello-pangea/dnd";
import OrderDetails from "./OrderDetails";
import axios from "axios";
import Modal from "./Modal";

export default function OrderCard({
  order,
  index,
  expanded,
  onToggle,
  setIsAnyModalActive,
  refetchOrders,
}) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ track that user has clicked notify (so reject is allowed)
  const [hasNotified, setHasNotified] = useState(false);

  const openCancelModal = (e) => {
    e.stopPropagation();
    setShowCancelModal(true);
    setIsAnyModalActive?.(true);
    setHasNotified(false); // reset each time modal opens
  };

  const closeCancelModal = () => {
    if (loading) return;
    setShowCancelModal(false);
    setIsAnyModalActive?.(false);
    setHasNotified(false);
  };

  // ✅ Format phone for WhatsApp (Nigeria-friendly)
  const formatWhatsAppPhone = (phone) => {
    if (!phone) return "";
    let cleaned = String(phone).replace(/\D/g, "");

    // If number starts with 0 (e.g. 08012345678 → 2348012345678)
    if (cleaned.startsWith("0")) {
      cleaned = "234" + cleaned.slice(1);
    }

    // If already starts with 234, leave it
    return cleaned;
  };

  // ✅ open WhatsApp WITH a message, then mark as notified
  const handleNotifyCustomer = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const phone = formatWhatsAppPhone(order.customer_phone);

    if (!phone) {
      alert("Customer phone number not available.");
      return;
    }

    const customerName = order.customer_name || "there";
    const orderNo = order.order_number
      ? `#${order.order_number}`
      : `ID ${order.id}`;

    const message = `Hi ${customerName}, your order ${orderNo} has been cancelled and will not be processed. Sorry for any inconvenience.`;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank", "noopener,noreferrer");
    setHasNotified(true);
  };

  // ✅ final reject action (API call)
  const handleRejectOrder = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    if (!hasNotified) {
      alert(
        "Please notify the customer on WhatsApp before cancelling the order.",
      );
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        "https://admin.deedelicious.com/php/cancel-order.php",
        { order_id: order.id },
      );

      if (refetchOrders) {
        await refetchOrders();
      }

      closeCancelModal();
    } catch (err) {
      console.error("Failed to Cancel Order:", err);
      alert("Failed to Cancel Order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp icon in card (unchanged, still just opens chat)
  const handleWhatsAppCustomer = (e) => {
    e.stopPropagation();

    const phone = formatWhatsAppPhone(order.customer_phone);

    if (!phone) {
      alert("Customer phone number not available.");
      return;
    }

    const url = `https://wa.me/${phone}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Draggable draggableId={order.id.toString()} index={index}>
        {(provided) => (
          <li
            className={`order-card ${expanded ? "expanded" : ""}`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={onToggle}
          >
            <div className="order-date">
              <span className="order-date-text">
                {new Date(order.created_at).toLocaleDateString("en-GB")}
              </span>
              <span className="order-id">Ref: {order.id}</span>
            </div>

            <div className="order-header">
              <div className="order-header-left">
                <div className="order-header-top">
                  <p>
                    <strong>Order #{order.order_number}</strong>
                  </p>

                  {order.order_type === "Eat In" && order.table_number && (
                    <p>
                      <strong>Table: </strong> {order.table_number}
                    </p>
                  )}

                  <p>
                    <strong>{order.order_type}</strong>
                  </p>
                </div>

                <div className="order-header-bottom">
                  <p>
                    <strong>Customer Name: </strong>
                    {order.customer_name || "Unnamed Customer"}
                  </p>
                  <p className="order-total">
                    <strong>£{parseFloat(order.total_price).toFixed(2)}</strong>
                  </p>
                </div>
              </div>

              {/* Action Icons */}
              <div className="order-header-right">
                <i
                  className="fa-brands fa-whatsapp whatsapp-icon"
                  onClick={handleWhatsAppCustomer}
                  title="Message customer on WhatsApp"
                />

                <i
                  className="fa-regular fa-circle-xmark cancel-order-icon"
                  onClick={openCancelModal}
                  title="Cancel Order"
                />
              </div>
            </div>

            {expanded && <OrderDetails order={order} />}
          </li>
        )}
      </Draggable>

      {/* Reject Modal */}
      {showCancelModal && (
        <Modal
          title="Cancel Order"
          onClose={closeCancelModal}
          onSubmit={handleRejectOrder}
          showCancel={false}
          showSubmitButton={true}
          submitButtonClass="cancel-btn" 
          submitButtonText={
            loading ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Cancelling…
              </>
            ) : (
              "Cancel Order"
            )
          }
          className="cancel-order-modal"
        >
          <div className="delete-modal-heading">
            <p>
              Are you sure you want to cancel this order?
              <br />
              Please notify customer on: <strong>{order.customer_phone}</strong>
            </p>
          </div>

          {/* 🔵 Notify Button */}
          <div style={{ marginTop: "16px" }}>
            <button
              type="button"
              onClick={handleNotifyCustomer}
              className="profiles-btn add-btn"
              disabled={loading}
            >
              {hasNotified ? "Notified ✓" : "Notify on WhatsApp"}
            </button>
          </div>

          {!hasNotified && (
            <p style={{ fontSize: "13px", marginTop: "8px", color: "#888" }}>
              You must notify the customer before cancelling.
            </p>
          )}
        </Modal>
      )}
    </>
  );
}
